import React, { useState } from 'react';
import { TAItem, TASubLine } from '../../types';
import { INDICATOR_CONFIGS, TASettingRow } from '../../indicatorConfigs';
import ColorSwatch from '../common/ColorSwatch';
import SelectDropdown from '../common/SelectDropdown';
import SettingsCheckbox from '../common/SettingsCheckbox';
import '../../../../Components/Common/typography.css';
import './TechnicalAnalysisSettings.css';

interface IndicatorSettingsProps {
  ta: TAItem;
  onUpdate: (key: string, changes: Partial<TAItem>) => void;
}

const IndicatorSettings: React.FC<IndicatorSettingsProps> = ({
  ta,
  onUpdate,
}) => {
  const config = INDICATOR_CONFIGS[ta.key];

  // Local state for uncontrolled select rows (normal-type settings)
  const [localSelectValues, setLocalSelectValues] = useState<
    Record<number, string>
  >(() => {
    const init: Record<number, string> = {};
    config?.settings.forEach((s, i) => {
      if (s.inputType === 'select' && s.defaultValue != null)
        init[i] = String(s.defaultValue);
    });
    // Restore persisted values from TAItem
    if (ta.selectValues) {
      Object.entries(ta.selectValues).forEach(([k, v]) => {
        init[Number(k)] = v;
      });
    }
    return init;
  });

  // Local state for normal-type row colors (editable)
  const [localColors, setLocalColors] = useState<Record<number, string>>(() => {
    const init: Record<number, string> = {};
    config?.settings.forEach((s, i) => {
      if (s.color) init[i] = s.color;
    });
    return init;
  });

  // True when this indicator uses check rows (e.g. SMA, NVI).
  // In that case ta.lines[] maps to check rows only — leading normal rows are NOT
  // in ta.lines, so we must not use ta.lines[index] for them.
  const hasCheckRows = !!config?.settings.some((s) => s.type === 'check');
  const firstColorIdx =
    config?.settings.findIndex((s) => s.color != null) ?? -1;
  // Last index of a check-type row. Trailing normal rows (index > lastCheckIdx) are
  // appended to ta.lines at the same settings index, so ta.lines[index] is valid for them.
  // Leading/interleaved normal rows (index <= lastCheckIdx) are NOT in ta.lines.
  const lastCheckIdx =
    config?.settings.reduce((last, s, i) => (s.type === 'check' ? i : last), -1) ?? -1;

  // Whether this settings index maps to a ta.lines entry we can read/write directly.
  const hasLineAt = (index: number) =>
    (!hasCheckRows || index > lastCheckIdx) && ta.lines?.[index] !== undefined;

  const getColor = (index: number, defaultColor?: string) => {
    if (hasLineAt(index))
      return ta.lines![index].color || defaultColor || '';
    if (index === firstColorIdx)
      return ta.color || defaultColor || '';
    return localColors[index] ?? defaultColor ?? '';
  };

  const setColor = (index: number, color: string) => {
    setLocalColors((prev) => ({ ...prev, [index]: color }));
    if (hasLineAt(index)) {
      updateLine(index, { color });
    }
    if (index === firstColorIdx) {
      onUpdate(ta.key, { color });
    }
  };

  const getSelectValue = (
    index: number,
    setting: { inputOptions?: string[]; defaultValue?: string | number },
  ) =>
    localSelectValues[index] ??
    (setting.defaultValue != null
      ? String(setting.defaultValue)
      : (setting.inputOptions?.[0] ?? ''));

  const setSelectValue = (index: number, value: string) => {
    setLocalSelectValues((prev) => {
      const next = { ...prev, [index]: value };
      onUpdate(ta.key, { selectValues: next });
      return next;
    });
  };

  // Fallback: simple Period + Color view if no config found
  if (!config) {
    return (
      <div className="ta-settings-content" lang="en">
        <div className="ta-settings-content-header">
          <span className="body-s-regular ta-settings-label">SETTINGS</span>
          <span className="body-s-regular ta-settings-label">COLOUR</span>
        </div>
        <div className="ta-settings-row">
          <span className="body-s-regular ta-settings-field-label">Period</span>
          <input
            type="number"
            className="ta-number-input"
            value={ta.period}
            min={1}
            max={999}
            onChange={(e) =>
              onUpdate(ta.key, { period: Number(e.target.value) })
            }
          />
          <ColorSwatch
            color={ta.color}
            onChange={(color) => onUpdate(ta.key, { color })}
            size={32}
            className="ta-color-swatch-common"
          />
        </div>
      </div>
    );
  }

  // Controlled rendering for check-type rows (SMA, WMA, EMA, etc.)
  const updateLine = (lineIdx: number, patch: Partial<TASubLine>) => {
    const updated = (ta.lines ?? []).map((l, i) =>
      i === lineIdx ? { ...l, ...patch } : l,
    );
    onUpdate(ta.key, { lines: updated });
  };

  const renderSettingRow = (setting: TASettingRow, index: number) => {
    // True when this indicator has any paired rows (same label, second has inputType:'none').
    // Normal-type single-swatch rows need a 34px spacer before their swatch so their input
    // aligns with the paired rows' input column. Check-type rows already use this spacer.
    const hasPaired = config.settings.some(
      (s, i) =>
        i < config.settings.length - 1 &&
        config.settings[i + 1].label === s.label &&
        config.settings[i + 1].inputType === 'none',
    );

    // Controlled check row — driven by ta.lines state
    if (setting.type === 'check' && ta.lines) {
      const lineIdx = config.settings
        .slice(0, index)
        .filter((s) => s.type === 'check').length;
      const line = ta.lines[lineIdx];
      if (!line) return null;
      return (
        <div
          key={index}
          className={`ta-settings-row${!line.enabled ? ' ta-settings-row--disabled' : ''}`}
        >
          <SettingsCheckbox
            checked={line.enabled}
            onChange={() => updateLine(lineIdx, { enabled: !line.enabled })}
          />
          <span className="body-s-regular ta-settings-field-label">
            {setting.label}
          </span>
          {setting.inputType === 'select' && setting.inputOptions && (
            <SelectDropdown
              options={setting.inputOptions.map((o) => ({ label: o, value: o }))}
              value={getSelectValue(lineIdx, setting)}
              onChange={(v) => setSelectValue(lineIdx, String(v))}
            />
          )}
          {setting.secondaryInput !== undefined && (
            <input
              type="number"
              className="ta-number-input"
              value={line.period}
              min={1}
              max={9999}
              onChange={(e) =>
                updateLine(lineIdx, { period: Number(e.target.value) })
              }
            />
          )}
          {setting.inputType === 'number' && (
            <input
              type="number"
              className="ta-number-input"
              value={line.period}
              min={1}
              max={9999}
              onChange={(e) =>
                updateLine(lineIdx, { period: Number(e.target.value) })
              }
            />
          )}
          {hasPaired && <span style={{ width: 34, flexShrink: 0 }} />}
          <ColorSwatch
            color={line.color}
            onChange={(color) => updateLine(lineIdx, { color })}
            size={32}
            className="ta-color-swatch-common"
          />
        </div>
      );
    }

    // Uncontrolled rendering for normal-type rows (MACD, BB, etc.)
    return (
      <div key={index} className="ta-settings-row">
        {/* Label */}
        <span className="body-s-regular ta-settings-field-label">
          {setting.label}
        </span>

        {/* Input: number or select */}
        {setting.inputType === 'number' &&
          (() => {
            const step =
              typeof setting.defaultValue === 'number' &&
              setting.defaultValue < 1
                ? 0.01
                : 1;
            const line = ta.lines?.[index];
            return line !== undefined ? (
              <input
                type="number"
                className="ta-number-input"
                value={line.period}
                min={0}
                max={9999}
                step={step}
                onChange={(e) =>
                  updateLine(index, { period: Number(e.target.value) })
                }
              />
            ) : (
              <input
                type="number"
                className="ta-number-input"
                defaultValue={setting.defaultValue as number}
                min={0}
                max={9999}
                step={step}
              />
            );
          })()}
        {setting.inputType === 'select' && setting.inputOptions && (
          <>
            <SelectDropdown
              options={setting.inputOptions.map((o) => ({
                label: o,
                value: o,
              }))}
              value={getSelectValue(index, setting)}
              onChange={(v) => setSelectValue(index, String(v))}
            />
            {setting.secondaryInput &&
              (() => {
                const line = ta.lines?.[index];
                return line !== undefined ? (
                  <input
                    type="number"
                    className="ta-number-input"
                    value={line.period}
                    min={0}
                    max={9999}
                    onChange={(e) =>
                      updateLine(index, { period: Number(e.target.value) })
                    }
                  />
                ) : (
                  <input
                    type="number"
                    className="ta-number-input"
                    defaultValue={setting.secondaryInput.defaultValue}
                    min={0}
                    max={9999}
                  />
                );
              })()}
          </>
        )}

        {/* Color swatch — when indicator has paired rows, add spacer before swatch
            so this row's input aligns with the paired rows' input column */}
        {setting.color ? (
          <>
            {hasPaired && <span style={{ width: 34, flexShrink: 0 }} />}
            <div className="ta-color-group">
              {setting.colorLabel && (
                <span className="body-m-regular ta-color-label">
                  {setting.colorLabel}
                </span>
              )}
              <ColorSwatch
                color={getColor(index, setting.color)}
                onChange={(color) => setColor(index, color)}
                size={32}
                className="ta-color-swatch-common"
              />
            </div>
          </>
        ) : setting.inputType === 'number' || setting.inputType === 'select' ? (
          <span
            className="ta-swatch-spacer"
            style={hasPaired ? { width: 80 } : undefined}
          />
        ) : null}
      </div>
    );
  };

  return (
    <div className="ta-settings-content" lang="en">
      <div className="ta-settings-content-header">
        <span className="body-s-regular ta-settings-label">SETTINGS</span>
        <span className="body-s-regular ta-settings-label">COLOUR</span>
      </div>

      {config.settings.map((setting, idx) => {
        // Check if next row has same label (for paired colors like σ+/σ-)
        const nextSetting = config.settings[idx + 1];
        const prevSetting = idx > 0 ? config.settings[idx - 1] : null;

        // Skip σ- style second-of-pair (inputType 'none' with same label as previous)
        if (
          prevSetting &&
          prevSetting.label === setting.label &&
          setting.inputType === 'none'
        ) {
          return null;
        }

        // A row is paired only when next row has same label AND inputType 'none'
        // (the σ+/σ- style — not coincidental same-label rows like CO/EOM/FI)
        const isPaired = !!(
          nextSetting &&
          nextSetting.label === setting.label &&
          nextSetting.inputType === 'none'
        );

        const isFirstPair =
          isPaired && (!prevSetting || prevSetting.label !== setting.label);
        const prevPrevSetting = idx > 1 ? config.settings[idx - 2] : null;
        const wasPreviousPaired = !!(
          prevSetting &&
          prevPrevSetting &&
          prevPrevSetting.label === prevSetting.label &&
          prevSetting.inputType === 'none'
        );

        if (isPaired) {
          const lineIdx = config.settings
            .slice(0, idx)
            .filter((s) => s.type === 'check').length;
          const line = ta.lines?.[lineIdx];
          const isEnabled = line?.enabled ?? true;

          const togglePair = () => {
            if (!ta.lines) return;
            const next = !isEnabled;
            const updated = ta.lines.map((l, i) =>
              i === lineIdx || i === lineIdx + 1 ? { ...l, enabled: next } : l,
            );
            onUpdate(ta.key, { lines: updated });
          };

          return (
            <React.Fragment key={idx}>
              {isFirstPair &&
                !wasPreviousPaired &&
                setting.colorLabel &&
                nextSetting.colorLabel && (
                  <div className="ta-settings-row ta-color-header-row">
                    <span className="ta-settings-field-label" />
                    <span className="ta-color-header-spacer" />
                    <span className="body-m-regular ta-color-label">
                      {setting.colorLabel}
                    </span>
                    <span className="body-m-regular ta-color-label">
                      {nextSetting.colorLabel}
                    </span>
                  </div>
                )}
              <div
                className={`ta-settings-row${!isEnabled ? ' ta-settings-row--disabled' : ''}`}
              >
                {setting.type === 'check' && (
                  <SettingsCheckbox checked={isEnabled} onChange={togglePair} />
                )}
                <span className="body-s-regular ta-settings-field-label">
                  {setting.label}
                </span>
                {setting.inputType === 'number' &&
                  (line !== undefined ? (
                    <input
                      type="number"
                      lang="en"
                      className="ta-number-input"
                      value={line.period}
                      min={0}
                      max={9999}
                      onChange={(e) =>
                        updateLine(lineIdx, { period: Number(e.target.value) })
                      }
                    />
                  ) : (
                    <input
                      type="number"
                      lang="en"
                      className="ta-number-input"
                      defaultValue={setting.defaultValue as number}
                      min={0}
                      max={9999}
                    />
                  ))}
                {setting.inputType === 'select' && setting.inputOptions && (
                  <SelectDropdown
                    options={setting.inputOptions.map((o) => ({
                      label: o,
                      value: o,
                    }))}
                    value={getSelectValue(idx, setting)}
                    onChange={(v) => setSelectValue(idx, String(v))}
                  />
                )}
                {setting.color && (
                  <ColorSwatch
                    color={ta.lines?.[lineIdx]?.color ?? setting.color}
                    onChange={(color) => updateLine(lineIdx, { color })}
                    size={32}
                    className="ta-color-swatch-common"
                  />
                )}
                {nextSetting.color && (
                  <ColorSwatch
                    color={ta.lines?.[lineIdx + 1]?.color ?? nextSetting.color}
                    onChange={(color) => updateLine(lineIdx + 1, { color })}
                    size={32}
                    className="ta-color-swatch-common"
                  />
                )}
              </div>
            </React.Fragment>
          );
        }

        return renderSettingRow(setting, idx);
      })}

      {/* Pane height row — checkbox-placeholder + dynamic spacer to align with input column above */}
      {config.hasPaneHeight &&
        (() => {
          // Only indent when ALL rows are check-type (mixed rows → align with normal rows)
          const hasCheckRows = config.settings.every((s) => s.type === 'check');
          const hasPaired = config.settings.some(
            (s, i) =>
              i < config.settings.length - 1 &&
              config.settings[i + 1].label === s.label &&
              config.settings[i + 1].inputType === 'none',
          );
          // When ALL setting rows are inputType:'none' (e.g. WAD, Volume) there is no
          // separate "input column" — swatches sit flush-right. Push the dropdown to the
          // same right-edge position by using a flex:1 filler instead of a trailing spacer.
          const hasInputRows = config.settings.some(
            (s) => s.inputType !== 'none',
          );
          // 1 swatch = 34px, 2 swatches (σ+σ-) = 34+12+34 = 80px
          const spacerWidth = hasPaired ? 80 : 34;
          const dropdown = (
            <SelectDropdown
              options={[
                { label: 'High', value: 2 },
                { label: 'Medium', value: 1 },
                { label: 'Low', value: 0 },
              ]}
              value={ta.paneHeight ?? 1}
              onChange={(v) => onUpdate(ta.key, { paneHeight: Number(v) })}
              className="ta-pane-height-select"
            />
          );
          if (!hasInputRows) {
            return (
              <div className="ta-settings-row ta-pane-height-row">
                {hasCheckRows && <span className="ta-checkbox-indent" />}
                <span className="body-s-regular ta-settings-field-label">
                  Pane height
                </span>
                {dropdown}
              </div>
            );
          }
          return (
            <div className="ta-settings-row ta-pane-height-row">
              {hasCheckRows && <span className="ta-checkbox-indent" />}
              <span className="body-s-regular ta-settings-field-label">
                Pane height
              </span>
              {dropdown}
              <span
                className="ta-swatch-spacer"
                style={{ width: spacerWidth }}
              />
            </div>
          );
        })()}
    </div>
  );
};

export default IndicatorSettings;
